// Market Data Service for Live Asset Prices
class MarketDataService {
  constructor() {
    this.baseURL = 'https://api.coingecko.com/api/v3';
    this.yahooFinanceAPI = 'https://query1.finance.yahoo.com/v8/finance/chart';
    this.cache = new Map();
    this.cacheTimeout = 30000; // 30 seconds
  }

  // Get live cryptocurrency prices
  async getCryptoPrices() {
    const cacheKey = 'crypto-prices';
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      const response = await fetch(
        `${this.baseURL}/simple/price?ids=bitcoin,ethereum,solana,cardano,polkadot,polygon&vs_currencies=usd&include_24hr_change=true&include_24hr_vol=true`
      );
      
      if (!response.ok) throw new Error('Failed to fetch crypto prices');
      
      const data = await response.json();
      
      const formattedData = [
        {
          symbol: 'BTC',
          name: 'Bitcoin',
          price: data.bitcoin?.usd || 0,
          change: data.bitcoin?.usd_24h_change || 0,
          volume: data.bitcoin?.usd_24h_vol || 0,
          positive: (data.bitcoin?.usd_24h_change || 0) >= 0
        },
        {
          symbol: 'ETH',
          name: 'Ethereum',
          price: data.ethereum?.usd || 0,
          change: data.ethereum?.usd_24h_change || 0,
          volume: data.ethereum?.usd_24h_vol || 0,
          positive: (data.ethereum?.usd_24h_change || 0) >= 0
        },
        {
          symbol: 'SOL',
          name: 'Solana',
          price: data.solana?.usd || 0,
          change: data.solana?.usd_24h_change || 0,
          volume: data.solana?.usd_24h_vol || 0,
          positive: (data.solana?.usd_24h_change || 0) >= 0
        },
        {
          symbol: 'ADA',
          name: 'Cardano',
          price: data.cardano?.usd || 0,
          change: data.cardano?.usd_24h_change || 0,
          volume: data.cardano?.usd_24h_vol || 0,
          positive: (data.cardano?.usd_24h_change || 0) >= 0
        },
        {
          symbol: 'DOT',
          name: 'Polkadot',
          price: data.polkadot?.usd || 0,
          change: data.polkadot?.usd_24h_change || 0,
          volume: data.polkadot?.usd_24h_vol || 0,
          positive: (data.polkadot?.usd_24h_change || 0) >= 0
        },
        {
          symbol: 'MATIC',
          name: 'Polygon',
          price: data.polygon?.usd || 0,
          change: data.polygon?.usd_24h_change || 0,
          volume: data.polygon?.usd_24h_vol || 0,
          positive: (data.polygon?.usd_24h_change || 0) >= 0
        }
      ];

      this.cache.set(cacheKey, {
        data: formattedData,
        timestamp: Date.now()
      });

      return formattedData;
    } catch (error) {
      console.error('Error fetching crypto prices:', error);
      return this.getFallbackCryptoData();
    }
  }

  // Get stock market data using Yahoo Finance API
  async getStockPrices() {
    const cacheKey = 'stock-prices';
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    const symbols = ['AAPL', 'GOOGL', 'MSFT', 'TSLA', 'NVDA', 'AMZN', 'META', 'NFLX'];
    
    try {
      const stockPromises = symbols.map(symbol => this.fetchYahooFinanceData(symbol));
      const stockResults = await Promise.allSettled(stockPromises);
      
      const formattedData = stockResults
        .map((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            return result.value;
          }
          // Fallback data for failed requests
          return this.getFallbackStockData(symbols[index]);
        })
        .filter(Boolean);

      this.cache.set(cacheKey, {
        data: formattedData,
        timestamp: Date.now()
      });

      return formattedData;
    } catch (error) {
      console.error('Error fetching stock prices:', error);
      return this.getFallbackStockData();
    }
  }

  // Fetch individual stock data from Yahoo Finance
  async fetchYahooFinanceData(symbol) {
    try {
      const response = await fetch(`${this.yahooFinanceAPI}/${symbol}?interval=1d&range=2d`);
      
      if (!response.ok) throw new Error(`Failed to fetch ${symbol}`);
      
      const data = await response.json();
      const chart = data.chart?.result?.[0];
      
      if (!chart) throw new Error(`No chart data for ${symbol}`);
      
      const meta = chart.meta;
      const currentPrice = meta.regularMarketPrice || meta.previousClose;
      const previousClose = meta.previousClose;
      const change = currentPrice - previousClose;
      const changePercent = (change / previousClose) * 100;
      
      return {
        symbol: symbol,
        name: this.getCompanyName(symbol),
        price: currentPrice.toFixed(2),
        change: change.toFixed(2),
        changePercent: changePercent.toFixed(2),
        volume: this.formatVolume(meta.regularMarketVolume || 0),
        positive: change >= 0,
        marketCap: meta.marketCap ? this.formatMarketCap(meta.marketCap) : 'N/A',
        high: meta.regularMarketDayHigh?.toFixed(2) || 'N/A',
        low: meta.regularMarketDayLow?.toFixed(2) || 'N/A'
      };
    } catch (error) {
      console.error(`Error fetching ${symbol}:`, error);
      return null;
    }
  }

  // Get company names
  getCompanyName(symbol) {
    const names = {
      'AAPL': 'Apple Inc.',
      'GOOGL': 'Alphabet Inc.',
      'MSFT': 'Microsoft Corp.',
      'TSLA': 'Tesla Inc.',
      'NVDA': 'NVIDIA Corp.',
      'AMZN': 'Amazon.com Inc.',
      'META': 'Meta Platforms Inc.',
      'NFLX': 'Netflix Inc.'
    };
    return names[symbol] || symbol;
  }

  // Get enhanced crypto data with more details
  async getEnhancedCryptoPrices() {
    const cacheKey = 'enhanced-crypto-prices';
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      const response = await fetch(
        `${this.baseURL}/coins/markets?vs_currency=usd&ids=bitcoin,ethereum,solana,cardano,polkadot,polygon,chainlink,avalanche-2&order=market_cap_desc&per_page=8&page=1&sparkline=false&price_change_percentage=24h,7d`
      );
      
      if (!response.ok) throw new Error('Failed to fetch enhanced crypto prices');
      
      const data = await response.json();
      
      const formattedData = data.map(coin => ({
        symbol: coin.symbol.toUpperCase(),
        name: coin.name,
        price: coin.current_price,
        change: coin.price_change_percentage_24h || 0,
        change7d: coin.price_change_percentage_7d_in_currency || 0,
        volume: coin.total_volume || 0,
        marketCap: coin.market_cap || 0,
        rank: coin.market_cap_rank,
        positive: (coin.price_change_percentage_24h || 0) >= 0,
        positive7d: (coin.price_change_percentage_7d_in_currency || 0) >= 0,
        image: coin.image,
        high24h: coin.high_24h,
        low24h: coin.low_24h
      }));

      this.cache.set(cacheKey, {
        data: formattedData,
        timestamp: Date.now()
      });

      return formattedData;
    } catch (error) {
      console.error('Error fetching enhanced crypto prices:', error);
      return this.getFallbackEnhancedCryptoData();
    }
  }

  // Get market indices with real data simulation
  async getMarketIndices() {
    const cacheKey = 'market-indices';
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    // Enhanced market indices with more realistic simulation
    const baseData = [
      { symbol: 'SPX', name: 'S&P 500', basePrice: 4567.89 },
      { symbol: 'IXIC', name: 'NASDAQ', basePrice: 14321.45 },
      { symbol: 'DJI', name: 'Dow Jones', basePrice: 33875.40 },
      { symbol: 'VIX', name: 'VIX', basePrice: 18.42 }
    ];

    const formattedData = baseData.map(index => {
      const change = (Math.random() - 0.5) * (index.symbol === 'VIX' ? 4 : 100);
      const changePercent = (change / index.basePrice) * 100;
      
      return {
        symbol: index.symbol,
        name: index.name,
        price: (index.basePrice + change).toFixed(2),
        change: change.toFixed(2),
        changePercent: changePercent.toFixed(2),
        positive: change >= 0
      };
    });

    this.cache.set(cacheKey, {
      data: formattedData,
      timestamp: Date.now()
    });

    return formattedData;
  }

  // Fallback data when API fails
  getFallbackCryptoData() {
    return [
      { symbol: 'BTC', name: 'Bitcoin', price: 67423.45, change: 2.34, volume: 28400000000, positive: true },
      { symbol: 'ETH', name: 'Ethereum', price: 3789.21, change: 1.87, volume: 15200000000, positive: true },
      { symbol: 'SOL', name: 'Solana', price: 198.76, change: -0.45, volume: 3800000000, positive: false },
      { symbol: 'ADA', name: 'Cardano', price: 0.5632, change: 4.21, volume: 892000000, positive: true },
      { symbol: 'DOT', name: 'Polkadot', price: 7.89, change: 2.14, volume: 654000000, positive: true },
      { symbol: 'MATIC', name: 'Polygon', price: 0.9876, change: -1.23, volume: 543000000, positive: false }
    ];
  }

  // Enhanced fallback data
  getFallbackStockData(symbol = null) {
    const fallbackStocks = [
      { symbol: 'AAPL', name: 'Apple Inc.', price: 175.43, change: 2.34, volume: '45.2M', marketCap: '2.7T' },
      { symbol: 'GOOGL', name: 'Alphabet Inc.', price: 2847.63, change: -15.23, volume: '1.2M', marketCap: '1.8T' },
      { symbol: 'MSFT', name: 'Microsoft Corp.', price: 378.85, change: 4.21, volume: '22.1M', marketCap: '2.8T' },
      { symbol: 'TSLA', name: 'Tesla Inc.', price: 248.42, change: -8.76, volume: '89.4M', marketCap: '789B' },
      { symbol: 'NVDA', name: 'NVIDIA Corp.', price: 875.28, change: 12.45, volume: '35.7M', marketCap: '2.1T' },
      { symbol: 'AMZN', name: 'Amazon.com Inc.', price: 3342.88, change: -7.12, volume: '28.9M', marketCap: '1.7T' },
      { symbol: 'META', name: 'Meta Platforms Inc.', price: 487.23, change: 5.67, volume: '15.8M', marketCap: '1.2T' },
      { symbol: 'NFLX', name: 'Netflix Inc.', price: 645.12, change: -2.34, volume: '3.4M', marketCap: '287B' }
    ];

    if (symbol) {
      const stock = fallbackStocks.find(s => s.symbol === symbol);
      if (stock) {
        return {
          ...stock,
          changePercent: ((stock.change / stock.price) * 100).toFixed(2),
          positive: stock.change >= 0,
          high: (stock.price * 1.05).toFixed(2),
          low: (stock.price * 0.95).toFixed(2)
        };
      }
    }

    return fallbackStocks.map(stock => ({
      ...stock,
      changePercent: ((stock.change / stock.price) * 100).toFixed(2),
      positive: stock.change >= 0,
      high: (stock.price * 1.05).toFixed(2),
      low: (stock.price * 0.95).toFixed(2)
    }));
  }

  getFallbackEnhancedCryptoData() {
    return [
      { symbol: 'BTC', name: 'Bitcoin', price: 67423.45, change: 2.34, change7d: 5.67, volume: 28400000000, marketCap: 1320000000000, rank: 1, positive: true, positive7d: true, high24h: 68500, low24h: 66200 },
      { symbol: 'ETH', name: 'Ethereum', price: 3789.21, change: 1.87, change7d: 3.21, volume: 15200000000, marketCap: 455000000000, rank: 2, positive: true, positive7d: true, high24h: 3850, low24h: 3720 },
      { symbol: 'SOL', name: 'Solana', price: 198.76, change: -0.45, change7d: 8.90, volume: 3800000000, marketCap: 89000000000, rank: 5, positive: false, positive7d: true, high24h: 205, low24h: 195 },
      { symbol: 'ADA', name: 'Cardano', price: 0.587, change: 3.21, change7d: -1.23, volume: 890000000, marketCap: 20600000000, rank: 8, positive: true, positive7d: false, high24h: 0.595, low24h: 0.568 },
      { symbol: 'DOT', name: 'Polkadot', price: 7.89, change: -1.23, change7d: 4.56, volume: 234000000, marketCap: 10200000000, rank: 12, positive: false, positive7d: true, high24h: 8.12, low24h: 7.65 },
      { symbol: 'MATIC', name: 'Polygon', price: 0.923, change: 4.56, change7d: 7.89, volume: 567000000, marketCap: 9100000000, rank: 14, positive: true, positive7d: true, high24h: 0.945, low24h: 0.898 },
      { symbol: 'LINK', name: 'Chainlink', price: 14.67, change: 2.11, change7d: -2.34, volume: 456000000, marketCap: 8600000000, rank: 16, positive: true, positive7d: false, high24h: 15.12, low24h: 14.23 },
      { symbol: 'AVAX', name: 'Avalanche', price: 42.18, change: -3.45, change7d: 6.78, volume: 789000000, marketCap: 16800000000, rank: 10, positive: false, positive7d: true, high24h: 44.56, low24h: 41.23 }
    ];
  }

  // Format volume for display
  formatVolume(volume) {
    if (volume >= 1e9) return `${(volume / 1e9).toFixed(1)}B`;
    if (volume >= 1e6) return `${(volume / 1e6).toFixed(1)}M`;
    if (volume >= 1e3) return `${(volume / 1e3).toFixed(1)}K`;
    return volume.toString();
  }

  // Format market cap for display
  formatMarketCap(marketCap) {
    if (marketCap >= 1e12) return `${(marketCap / 1e12).toFixed(1)}T`;
    if (marketCap >= 1e9) return `${(marketCap / 1e9).toFixed(1)}B`;
    if (marketCap >= 1e6) return `${(marketCap / 1e6).toFixed(1)}M`;
    return marketCap.toString();
  }

  // Format large numbers
  formatNumber(num) {
    if (num >= 1e9) {
      return (num / 1e9).toFixed(1) + 'B';
    }
    if (num >= 1e6) {
      return (num / 1e6).toFixed(1) + 'M';
    }
    if (num >= 1e3) {
      return (num / 1e3).toFixed(1) + 'K';
    }
    return num.toString();
  }

  // Format currency
  formatCurrency(amount, decimals = 2) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(amount);
  }

  // Format percentage
  formatPercentage(value) {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  }
}

export default new MarketDataService(); 